import {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  ColumnFilterRegistration,
  ColumnFilterRegistryContextType,
} from '../../../../../types/context';
import { CLOSE_OTHER_COLUMN_FILTERS_EVENT } from '../../../../../constants';

const defaultValues: ColumnFilterRegistryContextType = {
  isProvided: false,
  registerTriggerRef: () => {
    throw new Error('registerTriggerRef must be implemented');
  },
  consumePendingFocus: () => {
    throw new Error('consumePendingFocus must be implemented');
  },
  peekPendingFocus: () => {
    throw new Error('peekPendingFocus must be implemented');
  },
  registerApplyHandler: () => {
    throw new Error('registerApplyHandler must be implemented');
  },
  openColumnFilter: () => {
    throw new Error('openColumnFilter must be implemented');
  },
  closeColumnFilter: () => {
    throw new Error('closeColumnFilter must be implemented');
  },
  getTriggerElement: () => {
    throw new Error('getTriggerElement must be implemented');
  },
  appendPendingInitialChar: () => {
    throw new Error('appendPendingInitialChar must be implemented');
  },
  applyColumnFilter: () => {
    throw new Error('applyColumnFilter must be implemented');
  },
  markFilterSubmitted: () => {
    throw new Error('markFilterSubmitted must be implemented');
  },
  takeFilterSubmitted: () => {
    throw new Error('takeFilterSubmitted must be implemented');
  },
};

export const ColumnFilterRegistryContext =
  createContext<ColumnFilterRegistryContextType>(defaultValues);

// Bridges the quick-filter shortcut (lives outside the overlay) with each column's filter overlay: open it, focus/prefill its input, run its Apply.
const ColumnFilterRegistryContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Per-column trigger / apply handlers; a ref (not state) so registering doesn't re-render.
  const registrationsRef = useRef(new Map<string, ColumnFilterRegistration>());
  // Column just opened by the shortcut + the chars typed before its input mounted. Empty `initialChar` with a non-null payload means the shortcut opened the overlay without injecting a char (e.g. Space) — consumers interpret that.
  const pendingFocusColumnRef = useRef<{
    columnName: string;
    initialChar: string;
  } | null>(null);
  // Per-column flag set by FilterApplyTemplate after a successful Apply; FilterTemplate's unmount reads it to decide whether to revert an unconfirmed draft.
  const submittedColumnsRef = useRef(new Set<string>());

  // Looks up the column's registration or creates an empty slot on demand so register* methods can share one map entry.
  const getRegistration = useCallback((columnName: string) => {
    const existing = registrationsRef.current.get(columnName);
    if (existing) return existing;

    const next: ColumnFilterRegistration = {};
    registrationsRef.current.set(columnName, next);
    return next;
  }, []);

  // Stores the menu-button ref so the shortcut can click it; cleanup only detaches when the same ref is still attached.
  const registerTriggerRef = useCallback(
    (columnName: string, triggerRef: RefObject<HTMLElement | null>) => {
      const registration = getRegistration(columnName);
      registration.triggerRef = triggerRef;

      return () => {
        const current = registrationsRef.current.get(columnName);
        if (current?.triggerRef === triggerRef) {
          current.triggerRef = undefined;
        }
      };
    },
    [getRegistration]
  );

  // FilterTemplate calls this once its input mounts: returns the buffered chars ('' if none) and clears them, or null if this column wasn't shortcut-opened.
  const consumePendingFocus = useCallback((columnName: string) => {
    const pending = pendingFocusColumnRef.current;
    if (!pending || pending.columnName !== columnName) return null;

    pendingFocusColumnRef.current = null;
    return pending.initialChar;
  }, []);

  // Read-only counterpart of consumePendingFocus for consumers (e.g. ReferenceInput) that need to inspect the buffer without taking ownership of it.
  const peekPendingFocus = useCallback((columnName: string) => {
    const pending = pendingFocusColumnRef.current;
    if (!pending || pending.columnName !== columnName) return null;

    return pending.initialChar;
  }, []);

  // Stores the apply callback exposed by FilterApplyTemplate so Enter inside the overlay shares the same submit path as the Apply button.
  const registerApplyHandler = useCallback(
    (columnName: string, applyFilter: (() => void) | null) => {
      const registration = getRegistration(columnName);
      registration.applyFilter = applyFilter || undefined;

      return () => {
        const current = registrationsRef.current.get(columnName);
        if (current?.applyFilter === applyFilter) {
          current.applyFilter = undefined;
        }
      };
    },
    [getRegistration]
  );

  // Opens the overlay by clicking the column's menu button; stashes the typed char for FilterTemplate / ReferenceInput to pick up once the input mounts. Dispatches a window event so any other currently-open column-filter overlay (potentially in a sibling grid with its own registry) closes first — PrimeReact's outside-click doesn't always fire across grid boundaries.
  const openColumnFilter = useCallback(
    (columnName: string, initialChar?: string) => {
      const trigger =
        registrationsRef.current.get(columnName)?.triggerRef?.current;
      if (!trigger) return false;

      window.dispatchEvent(
        new CustomEvent(CLOSE_OTHER_COLUMN_FILTERS_EVENT, {
          detail: { except: columnName },
        })
      );

      pendingFocusColumnRef.current = {
        columnName,
        initialChar: initialChar ?? '',
      };
      trigger.click();
      return true;
    },
    []
  );

  // Closes the column filter overlay by clicking its menu button (PrimeReact treats it as toggle). Doesn't apply changes (only the Apply button does) and doesn't clear the already-applied filter (only the Clear button does).
  const closeColumnFilter = useCallback((columnName: string) => {
    const trigger =
      registrationsRef.current.get(columnName)?.triggerRef?.current;
    if (!trigger) return false;

    trigger.click();
    return true;
  }, []);

  const getTriggerElement = useCallback((columnName: string) => {
    return (
      registrationsRef.current.get(columnName)?.triggerRef?.current ?? null
    );
  }, []);

  // Appends a typed char to the pending focus buffer so rapid keystrokes between trigger and focus landing are not lost.
  const appendPendingInitialChar = useCallback(
    (columnName: string, char: string) => {
      const pending = pendingFocusColumnRef.current;
      if (!pending || pending.columnName !== columnName) return false;

      pending.initialChar += char;
      return true;
    },
    []
  );

  // Invokes the registered apply handler so Enter in the overlay matches clicking the Apply button.
  const applyColumnFilter = useCallback((columnName: string) => {
    const applyFilter = registrationsRef.current.get(columnName)?.applyFilter;
    if (!applyFilter) return false;

    applyFilter();
    return true;
  }, []);

  const markFilterSubmitted = useCallback((columnName: string) => {
    submittedColumnsRef.current.add(columnName);
  }, []);

  const takeFilterSubmitted = useCallback((columnName: string) => {
    const submitted = submittedColumnsRef.current.has(columnName);
    submittedColumnsRef.current.delete(columnName);
    return submitted;
  }, []);

  const value: ColumnFilterRegistryContextType = useMemo(
    () => ({
      isProvided: true,
      registerTriggerRef,
      registerApplyHandler,
      openColumnFilter,
      closeColumnFilter,
      getTriggerElement,
      appendPendingInitialChar,
      consumePendingFocus,
      peekPendingFocus,
      applyColumnFilter,
      markFilterSubmitted,
      takeFilterSubmitted,
    }),
    [
      registerTriggerRef,
      registerApplyHandler,
      openColumnFilter,
      closeColumnFilter,
      getTriggerElement,
      appendPendingInitialChar,
      consumePendingFocus,
      peekPendingFocus,
      applyColumnFilter,
      markFilterSubmitted,
      takeFilterSubmitted,
    ]
  );

  return (
    <ColumnFilterRegistryContext.Provider value={value}>
      {children}
    </ColumnFilterRegistryContext.Provider>
  );
};

export default ColumnFilterRegistryContextProvider;
